import {
  Column,
  ColumnChooser,
  DataGrid,
  Editing,
  Export,
  FilterRow,
  HeaderFilter,
  Item,
  Scrolling,
  SearchPanel,
  Selection,
  StateStoring,
  Toolbar,
  DataGridTypes,
  DataGridRef,
  Sorting,
  Search,
  FilterPanel,
  Pager,
  Button as GridButton,
} from 'devextreme-react/data-grid'
import ODataStore from 'devextreme/data/odata/store'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { exportDataGrid } from 'devextreme/excel_exporter'
import { Workbook } from 'exceljs'
import { saveAs } from 'file-saver'
import { useScreenSize } from '@/themes/media-query'
import notify from 'devextreme/ui/notify'
import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import dxDataGrid, {
  ColumnButtonClickEvent,
  ExportingEvent,
  RowDblClickEvent,
  RowPreparedEvent,
} from 'devextreme/ui/data_grid'
import { useLoaderData } from 'react-router-typesafe'
import { useNavigate, useParams } from 'react-router-dom'
import { packagesSaleGridRouteLoader } from '@/routes/packages/sale/PackagesSaleGrid.route'
import { GridCellColor } from '@/enums'
import { pacchetto_vendita } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { Popup } from 'devextreme-react'
import { ClickEvent } from 'devextreme/ui/button'
import { confirm } from 'devextreme/ui/dialog'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { Accordion, Item as AccordionItem } from 'devextreme-react/accordion'
import { ContentReadyEvent } from 'devextreme/ui/accordion'
import { FormFilterItemDescriptorType } from '@/components/filter-form/GenericFilterForm.types'
import GenericFilterForm from '@/components/filter-form/GenericFilterForm'
import { AxiosError, AxiosResponse } from 'axios'
import BalanceDateSelector from '@/routes/packages/sale/BalanceDateSelector'
import { useTokenRefresh } from '@/auth/azure/azureManager'
import DateColumn from '@/components/date-column/DateColumn'
import { columnSourceFactory, isUserRoleAllowed, onDataGridSelectedRowButtonClick } from '@/routes/utils'
import { Roles } from '@/auth/azure/Roles'
import { gridTotalItemsLabel } from '@/constants'
import DocumentHead from '@/components/document-head/DocumentHead'
import { ObjectCodeLink } from '@/components/object-code-link/ObjectCodeLink'

const PackagesSaleGrid = () => {
  const { title, aziende, userInfo, defaultCRUDAllowedRoles } = useLoaderData<typeof packagesSaleGridRouteLoader>()
  const token = useTokenRefresh()
  const { clientId } = useParams()
  const { service, client } = useQsAdminApiManager()
  const [balancePopupVisible, setBalancePopupVisible] = useState(false)
  const [computeBalanceData, setComputeBalanceData] = useState<any>()

  const gridRef = useRef<DataGridRef>(null)
  const currentScreenSize = useScreenSize()
  const navigate = useNavigate()
  const getGridHeight = useCallback(() => {
    let h = '72vh'
    if (currentScreenSize.isMedium) {
      h = '68vh'
    } else if (currentScreenSize.isSmall) {
      h = '120vh'
    } else if (currentScreenSize.isXSmall) {
      h = '65vh'
    }
    return h
  }, [currentScreenSize])

  const getFiltersConfiguration = useCallback((): FormFilterItemDescriptorType[] => {
    const fltConf: FormFilterItemDescriptorType[] = [
      {
        fieldName: 'azienda',
        placeHolder: 'Azienda',
        valueKeyName: 'id',
        valueDisplayExpr: 'nome',
        values: aziende.data.value,
        defaultValue: clientId ? aziende.data.value.find((azienda) => azienda.id === Number(clientId))?.id : undefined,
        readOnly: clientId ? true : false,
        composeFilterItem(): any[] | null {
          if (this.currentValue === undefined || this.currentValue === null) return null
          return [['sede/azienda/id', this.currentValue]]
        },
      },
      {
        fieldName: 'scaduto',
        placeHolder: 'Scaduto',
        valueKeyName: 'scaduto',
        defaultValue: 'Solo non scaduti',
        valueDisplayExpr: 'scaduto',
        values: [{ scaduto: 'Solo non scaduti' }],
        composeFilterItem(): any[] | null {
          if (!this.currentValue) return null
          const now: Date = new Date()
          const minDate: Date = new Date(now.getFullYear() - 2, 0, 1)
          const maxDate = now
          return [
            ['creazione', '<=', maxDate],
            ['creazione', '>=', minDate],
          ]
        },
      },
      {
        fieldName: 'residuo',
        placeHolder: 'Residuo',
        valueKeyName: 'residuo',
        defaultValue: 'Solo non esauriti',
        valueDisplayExpr: 'residuo',
        values: [{ residuo: 'Solo non esauriti' }],
        composeFilterItem(): any[] | null {
          if (!this.currentValue) return null
          return [['unita_restanti gt 0']]
        },
      },
      {
        fieldName: 'disabilitato',
        placeHolder: 'Disabilitato',
        valueKeyName: 'disabilitato',
        defaultValue: 'Solo non disabilitati',
        valueDisplayExpr: 'disabilitato',
        values: [{ disabilitato: 'Solo non disabilitati' }],
        composeFilterItem(): any[] | null {
          if (!this.currentValue) return null
          return [['disabilitato eq false']]
        },
      },
    ]
    console.log('GENFLT - GETCONF :', fltConf)
    return fltConf
  }, [aziende])

  const initialFilterConfiguration = useMemo(() => getFiltersConfiguration(), [getFiltersConfiguration])
  const [filterConfiguration, setFilterConfiguration] = useState(initialFilterConfiguration)
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false)
  const getCurrentFilter = useCallback((): any[] => {
    let applied: boolean = false
    const currentFlts = []
    for (const flt of filterConfiguration) {
      const fltValue = flt.composeFilterItem()
      if (fltValue !== null) {
        currentFlts.push(fltValue)
      }
      if (!applied && flt.currentValue !== flt.defaultValue) applied = true
    }
    setFiltersApplied(applied)
    console.log('GENFLT - CURRENT:', currentFlts)
    return currentFlts
  }, [filterConfiguration])

  const [currentFilter, setCurrentFilter] = useState<any[]>([])

  const packagesSaleDataSource = {
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/pacchetto_vendita`,
      key: 'id',
      keyType: 'Int32',
      version: 4,
      errorHandler: (e) => {
        console.log(e.errorDetails)
        notify(
          {
            message: `Errore : ${e.errorDetails?.message}`,
            type: 'error',
            displayTime: 5000,
          },
          {
            position: 'bottom center',
            direction: 'up-push',
          },
        )
      },
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    filter: currentFilter.length > 0 ? currentFilter : null,
    expand: ['commessa', 'sede($expand=azienda)', 'utilizzi', 'tipologia_unita', 'ordine'],
  }
  const onRowPrepared = (e: RowPreparedEvent) => {
    if (e.rowType === 'data' && e.data.consuntivo && e.data.consuntivo === true) {
      e.rowElement.style.cssText = `${e.rowElement.style.cssText}color: black;background-color: ${GridCellColor.SKYBLUE}`
    }
  }
  const onExporting = (e: ExportingEvent) => {
    ExportDataGridToExcel(e.component)
  }

  const onDeleteClick = (e: ClickEvent) => {
    onDataGridSelectedRowButtonClick(gridRef, (rowdata: pacchetto_vendita) => {
      const result = confirm(
        `<i>Vuoi davvero eliminare il pacchetto <b>${rowdata.ded_Dis}</b>?</i>`,
        'Eliminazione pacchetto',
      )
      result.then((dialogResult) => {
        if (dialogResult === false) return
        service
          .pacchetto_vendita(rowdata.id)
          .delete()
          .then(() => {
            // Aggiornamento griglia
            gridRef.current?.instance().refresh()

            notify(
              {
                message: `Pacchetto '${rowdata['ded_Dis']}'  eliminato con successo`,
                type: 'success',
                displayTime: 5000,
              },
              {
                position: 'bottom center',
                direction: 'up-push',
              },
            )
          })
          .catch((error: unknown) => {
            notify(
              {
                message: `Errore eliminazione pacchetto: ${error}`,
                type: 'error',
                displayTime: 5000,
              },
              {
                position: 'bottom center',
                direction: 'up-push',
              },
            )
          })
      })
    })
  }

  const handleBalanceReportClick = (e: ClickEvent) => {
    setComputeBalanceData({
      data: {
        refDate: new Date(),
      },
      onSubmitEditor: async (e: any, balanceCriteria: any) => {
        e.preventDefault()
        try {
          const response = await client.get('/api/pacchettovendita/balancereport', {
            params: { refDate: balanceCriteria.refDate },
            headers: {
              'Content-Disposition': `attachment;filename=QsBilancioPacchetti.xlsx`,
              'Content-Type': 'application/octet-stream',
            },
            responseType: 'arraybuffer',
          })
          saveAs(new Blob([response.data], { type: 'application/octet-stream' }), `QsBilancioPacchetti.xlsx`)
        } catch (error: unknown) {
          notify(`Errore esportazione report bilancio. Dettagli : ${error}`, 'error', 2000)
        } finally {
          setBalancePopupVisible(false)
        }
      },
    })

    setBalancePopupVisible(true)
  }

  const handleUpdateUnitLeftClick = (e: ClickEvent) => {
    gridRef?.current?.instance().beginCustomLoading('')
    client
      .post('/api/pacchettovendita/updateunitleft')
      .then((response: AxiosResponse) => {
        notify('Calcolo delle unità residue effettuato con successo', 'success', 2000)
      })
      .catch((error: AxiosError) => {
        notify(`Errore calcolo delle unità residue. Dettagli : ${error}`, 'error', 2000)
      })
      .finally(() => {
        gridRef?.current?.instance().endCustomLoading()
        gridRef?.current?.instance().refresh()
      })
  }

  const handleForceUpdateUnitLeftClick = (e: ClickEvent) => {
    onDataGridSelectedRowButtonClick(gridRef, (rowdata: pacchetto_vendita) => {
      gridRef?.current?.instance().beginCustomLoading('')
      client
        .put(`/api/pacchettovendita/forceupdateunitleft`, rowdata.id, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((response: AxiosResponse) => {
          notify('Calcolo delle unità residue effettuato con successo', 'success', 2000)
        })
        .catch((error: AxiosError) => {
          notify(`Errore calcolo delle unità residue. Dettagli : ${error}`, 'error', 2000)
        })
        .finally(() => {
          gridRef?.current?.instance().endCustomLoading()
          gridRef?.current?.instance().refresh()
        })
    })
  }

  const ExportDataGridToExcel = (component: dxDataGrid) => {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet('Pacchetti vendita')
    exportDataGrid({ component, worksheet }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'PacchettiVendita.xlsx')
      })
    })
  }

  useEffect(() => {
    getCurrentFilter()
  }, [])

  return (
    <>
      <div className="accordion-generic-filter-form">
        <Accordion
          className={filtersApplied ? 'with-filter' : ''}
          collapsible={true}
          onContentReady={(e: ContentReadyEvent<any, any>) => {
            e.component?.collapseItem(0)
          }}
        >
          <AccordionItem icon="filter" title={'Filtri'}>
            <GenericFilterForm
              FormItemDescriptors={filterConfiguration}
              resetFilterClicked={function (e: ClickEvent): void {
                setFilterConfiguration(getFiltersConfiguration())
              }}
              filterValueChanged={function (): void {
                const currentFlts = getCurrentFilter()
                console.log('GENFLT - VALUE CHNG', currentFlts)
                if (JSON.stringify(currentFlts) !== JSON.stringify(currentFilter)) {
                  console.log('GENFLT - SETTING FILT', currentFlts)
                  setCurrentFilter(currentFlts)
                }
              }}
            />
          </AccordionItem>
        </Accordion>
      </div>
      <DataGrid
        id={clientId ? 'client-packages-sale-datagrid' : 'packages-sale-datagrid'}
        height={getGridHeight()}
        dataSource={packagesSaleDataSource}
        className={'grid dx-card wide-card'}
        showBorders={false}
        showColumnLines={true}
        wordWrapEnabled={false}
        showRowLines={true}
        focusedRowEnabled={true}
        rowAlternationEnabled={false}
        allowColumnResizing={true}
        allowColumnReordering={true}
        width="100%"
        onExporting={onExporting}
        onRowPrepared={onRowPrepared}
        ref={gridRef}
        columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
        noDataText="Nessun pacchetto trovato"
        remoteOperations={{
          filtering: true,
          grouping: false,
          groupPaging: false,
          paging: true,
          sorting: true,
          summary: true,
        }}
        grouping={{
          // Fondamentale per il raggruppamento veloce!!!
          autoExpandAll: true,
        }}
        groupPanel={{
          visible: currentScreenSize.isLarge || currentScreenSize.isMedium,
          emptyPanelText: 'Trascina qui una colonna per raggruppare',
        }}
        onRowDblClick={(e: RowDblClickEvent<any, any>) => {
          if (e.rowType === 'data') navigate(`${e.data.id}`)
        }}
      >
        <Pager
          visible={true}
          allowedPageSizes={'auto'}
          displayMode={'compact'}
          showPageSizeSelector={false}
          showInfo={true}
          showNavigationButtons={false}
          infoText={gridTotalItemsLabel}
        />
        <FilterRow visible={true} />
        <Sorting mode="multiple"></Sorting>
        <SearchPanel visible={true} width={currentScreenSize.isXSmall ? 180 : 250} />
        <Export enabled={true} />
        <FilterPanel visible={true} />
        <Editing allowAdding={true} />
        <HeaderFilter visible={true} />
        <Selection mode="single" />
        <ColumnChooser enabled={true} />
        <Scrolling mode="virtual" />
        <StateStoring
          enabled={true}
          type="localStorage"
          storageKey={clientId ? 'client-packages-sale-datagrid' : 'packages-sale-datagrid'}
          savingTimeout={50}
        />
        <Column
          visible={false}
          type="buttons"
          width={currentScreenSize.isXSmall || currentScreenSize.isSmall ? '10%' : '2%'}
          alignment="left"
          showInColumnChooser={false}
        >
          <GridButton
            hint="Details"
            icon="search"
            onClick={(e: ColumnButtonClickEvent) => {
              navigate(`${e.row?.data.id}`)
            }}
          ></GridButton>
        </Column>
        <Column
          dataField={'id'}
          caption="ID"
          defaultSortOrder="desc"
          visible={false}
          dataType="number"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 1 : undefined
          }
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'pacchetto_vendita', 'id', true)}></HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'consuntivo'}
          caption="C"
          allowHeaderFiltering={false}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 12 : undefined
          }
          width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 40 : undefined}
          minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 40 : undefined}
        ></Column>
        <Column
          dataField={'ded_Dis'}
          cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
            const data: pacchetto_vendita = cellData.row.data
            return (
              <div className={data.consuntivo && data.consuntivo === true ? `customLink` : ''}>
                <ObjectCodeLink
                  url={`${data.id}`}
                  text={data.ded_Dis}
                  disabled={!isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
                ></ObjectCodeLink>
              </div>
            )
          }}
          caption={'CODICE'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 13 : undefined
          }
          width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 90 : undefined}
          minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 90 : undefined}
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'pacchetto_vendita', 'ded_Dis', true)}>
              <Search enabled={true} searchExpr={'ded_Dis'} />
            </HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'sede.azienda.nome'}
          cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
            const data: pacchetto_vendita = cellData.row.data
            return (
              <div className={data.consuntivo && data.consuntivo === true ? `customLink` : ''}>
                <ObjectCodeLink
                  url={`/clients/${data.sede?.azienda?.id}`}
                  text={data.sede?.azienda?.nome}
                  disabled={clientId ? true : !isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
                ></ObjectCodeLink>
              </div>
            )
          }}
          caption={'AZIENDA'}
          visible={clientId === undefined}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 14 : undefined
          }
          width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 120 : undefined}
          minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 120 : undefined}
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'azienda', 'nome')}>
              <Search enabled={true} searchExpr={'nome'} />
            </HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'sede.nome'}
          caption={'SEDE'}
          allowHeaderFiltering={false}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 10 : undefined
          }
        />
        <Column
          dataField={'commessa.ded_Dis'}
          cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
            const data: pacchetto_vendita = cellData.row.data
            return (
              <div className={data.consuntivo && data.consuntivo === true ? `customLink` : ''}>
                <ObjectCodeLink
                  url={`/projects/${data.commessa?.id}`}
                  text={data.commessa?.ded_Dis}
                  disabled={
                    !isUserRoleAllowed(userInfo?.roles, [
                      ...defaultCRUDAllowedRoles,
                      Roles.Marketing,
                      Roles.Sales,
                      Roles.ExternalSales,
                    ])
                  }
                ></ObjectCodeLink>
              </div>
            )
          }}
          caption={'COMMESSA'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 3 : undefined
          }
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'commessa', 'ded_Dis', true)}>
              <Search enabled={true} searchExpr={'ded_Dis'} />
            </HeaderFilter>
          )}
        </Column>
        {DateColumn({
          dataField: 'creazione',
          caption: 'DATA',
          format: 'dd/MM/yyyy',
          width: 100,
          hidingPriority:
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 11 : undefined,
        })}
        {DateColumn({
          dataField: 'data_fatturazione',
          caption: 'DATA FATTURA',
          format: 'dd/MM/yyyy',
          hidingPriority:
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 2 : undefined,
        })}
        <Column
          dataField={'tipologia_unita.nome'}
          caption={'TIPOLOGIA UNITA'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 5 : undefined
          }
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'tipologia_durata_pacchetto', 'nome')}>
              <Search enabled={true} searchExpr={'nome'} />
            </HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'unita'}
          caption={'UNITA'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 6 : undefined
          }
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'pacchetto_vendita', 'unita', true)}></HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'costo_unita'}
          caption={'COSTO UNITARIO'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 7 : undefined
          }
        >
          {!clientId && (
            <HeaderFilter
              dataSource={columnSourceFactory(token, 'pacchetto_vendita', 'costo_unita', true)}
            ></HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'unita_restanti'}
          caption={'RESIDUO'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 8 : undefined
          }
        >
          {!clientId && (
            <HeaderFilter
              dataSource={columnSourceFactory(token, 'pacchetto_vendita', 'unita_restanti', true)}
            ></HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'note'}
          caption={'MOTIVO'}
          allowHeaderFiltering={false}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 9 : undefined
          }
        />
        <Column
          dataField={'ordine.ded_Dis'}
          cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
            const data: pacchetto_vendita = cellData.row.data
            return (
              <div className={data.consuntivo && data.consuntivo === true ? `customLink` : ''}>
                <ObjectCodeLink
                  url={`/orders/${data.ordine?.id}`}
                  text={data.ordine?.ded_Dis}
                  disabled={false}
                ></ObjectCodeLink>
              </div>
            )
          }}
          caption={'ORDINE'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 4 : undefined
          }
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'ordine', 'ded_Dis', true)}>
              <Search enabled={true} searchExpr={'ded_Dis'} />
            </HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'generico'}
          caption="GENERICO"
          allowHeaderFiltering={false}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 1 : undefined
          }
          width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 40 : undefined}
          minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 40 : undefined}
        ></Column>
        <Column
          dataField={'disabilitato'}
          caption="DISABILITATO"
          allowHeaderFiltering={false}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 1 : undefined
          }
          width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 40 : undefined}
          minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 40 : undefined}
        ></Column>
        <Toolbar>
          <Item
            location="before"
            locateInMenu="never"
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Refresh',
              text: 'Refresh',
              icon: 'refresh',
              onClick: (e: ClickEvent) => {
                gridRef.current?.instance().refresh()
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item name="groupPanel" />
          <Item
            visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            name="addRowButton"
            options={{
              onClick: () => {
                navigate(`new`)
              },
              hint: 'Nuovo pacchetto',
              text: 'Nuovo pacchetto',
            }}
          />
          <Item
            visible={isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.Administrator])}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Esporta bilancio pacchetti vendita',
              text: 'Esporta bilancio pacchetti vendita',
              icon: 'formula',
              onClick: handleBalanceReportClick,
              stylingMode: 'text',
            }}
          ></Item>
          <Item
            widget="dxDropDownButton"
            options={{
              icon: 'pulldown',
              hint: 'Aggiorna unità residue',
              stylingMode: 'text',
              dropDownOptions: { width: '250' },
              useItemTextAsTitle: false,
              items: [
                {
                  hint: 'Aggiorna unità residue',
                  text: 'Aggiorna unità residue',
                  icon: 'pulldown',
                  onClick: handleUpdateUnitLeftClick,
                  stylingMode: 'text',
                },
                {
                  hint: 'Forza aggiornamento singolo pv',
                  text: 'Forza aggiornamento singolo pv',
                  icon: 'refresh',
                  onClick: handleForceUpdateUnitLeftClick,
                  stylingMode: 'text',
                },
              ],
            }}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
          ></Item>
          <Item
            visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Elimina pacchetto',
              text: 'Elimina pacchetto',
              icon: 'trash',
              onClick: onDeleteClick,
              stylingMode: 'text',
            }}
          ></Item>
          <Item
            name="columnChooserButton"
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            options={{ hint: 'Scelta colonne', text: 'Scelta colonne' }}
          />
          <Item
            name="exportButton"
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            options={{ hint: 'Esporta dati in excel', text: 'Esporta dati in excel' }}
          />
          <Item name="searchPanel" />
        </Toolbar>
      </DataGrid>
      <Popup
        width={currentScreenSize.isLarge || currentScreenSize.isMedium ? 400 : currentScreenSize.isXSmall ? 350 : 700}
        height={'auto'}
        hideOnOutsideClick={true}
        showCloseButton={true}
        deferRendering={true}
        title={'Bilancio'}
        visible={balancePopupVisible}
        onHiding={useCallback(() => {
          setBalancePopupVisible(false)
        }, [setBalancePopupVisible])}
        className="computebalance-popup-content"
        resizeEnabled={false}
      >
        <BalanceDateSelector {...computeBalanceData} />
      </Popup>
    </>
  )
}

export default PackagesSaleGrid
